import { P2GAlert } from '@parcel2go/core-ui';
import { P2GBox } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { useEffect, useMemo, useState } from 'react';
import { ItemActions, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { HasClaims, HasRefund, IsReturnCollection } from '../../../lib/helpers/SmartSendSummaryGenerator';

import Loading from '../../../../../components/loading/Loading';
import { ItemActionType } from '../item/SetMarketplaceAction';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { PanelButtons } from './PanelButtons';
import { AsyncLinks } from '../actions/book/AsyncLinks';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { GetInTransitOrderActions, GetOrderActions } from './GetOrderActions';
import { P2GImageMessage } from '~/components/imagemessage';
import Labels from '../edit/Labels';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import PudoCodesAutoLoad from '../edit/PudoCodesAutoLoad';
import OrderInvoice from '../actions/book/OrderInvoice';

interface props {
  close?: () => void;
  actions: () => Promise<ItemActions>;
  setAction(type: ItemActionType);
  item: SmartSendItemApiModel;
  buyAgain: () => void;
  onCancel: () => void;
  onRaiseEnquiry: () => void;
  arrangeReturn: () => void;
}

export const OrderActionsPanel: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [actions, setActions] = useState<ItemActions>(null);
  const inTransit = !!props.item.TrackingSummary?.DroppedOff || !!props.item.TrackingSummary?.Collected;
  const [loading, setLoading] = useState<string[]>([]);

  const hasClaim = useMemo(() => HasClaims(props.item), []);
  const hasRefund = useMemo(() => HasRefund(props.item), []);

  const showInvoice =
    !hasRefund && props.item?.BookingDetails?.OrderId && !actions?.Links?.some((x) => x.Type.includes('INVOICE'));

  useEffect(() => {
    if (props.item) {
      props.actions().then(setActions);
    }
  }, [props.item]);

  const buyAgain = () => {
    if (loading.includes('BUY-AGAIN_ONCLICK')) {
      return;
    }
    setLoading([...loading, 'BUY-AGAIN_ONCLICK']);
    props.buyAgain();
  };

  const onCancel = () => {
    if (loading.includes('CANCEL_ONCLICK')) {
      return;
    }
    setLoading([...loading, 'BUY-CANCEL_ONCLICK']);
    props.onCancel();
  };

  const onRaiseEnquiry = () => {
    if (loading.includes('RAISE-ENQUIRY_ONCLICK')) {
      return;
    }
    setLoading([...loading, 'RAISE-ENQUIRY_ONCLICK']);
    props.onRaiseEnquiry();
  };

  const arrangeReturn = () => {
    if (loading.includes('ARRANGE-RETURN_ONCLICK')) {
      return;
    }
    setLoading([...loading, 'ARRANGE-RETURN_ONCLICK']);
    props.arrangeReturn();
  };

  if (!props.item || !props.item.BookingDetails || !props.item.BookingDetails.OrderLineId) {
    return (
      <SidePanelSection
        title={
          props.item.MarketplaceOrderLines.map((x) => x.MarketplaceOrderLineId).join(', ') || props.item.Description
        }
      >
        <P2GImageMessage
          image="/theme/smartsend/notifications/no-details-found.png"
          message="No Details Found"
          contentKey="image-smartsend-orderpanel-no-details-found"
        >
          <P2GAlert color="info">
            <P2GText>We don't have any booking details for this order.</P2GText>
          </P2GAlert>
        </P2GImageMessage>
      </SidePanelSection>
    );
  }

  return (
    <P2GBox textAlign="left">
      <SidePanelSections>
        {actions ? (
          <>
            {inTransit ? (
              <>
                {showInvoice && (
                  <OrderInvoice
                    title={t('INVOICE_ACTION_SUBTITLE', { defaultValue: 'Invoice' })}
                    orderId={Number(props.item.BookingDetails.OrderId)}
                    wrapper={(x) => x}
                  />
                )}
                <AsyncLinks
                  title="Post Purchase Actions"
                  variant="list"
                  links={[
                    ...actions.Links,
                    !IsReturnCollection(props.item) &&
                      SettingsCache.settings.Features.includes('ReturnCollection') && {
                        Label: 'Arrange\nReturn',
                        OnClick: arrangeReturn,
                        Type: 'ARRANGE-RETURN_ONCLICK',
                        Loading: loading.includes('ARRANGE-RETURN_ONCLICK')
                      },
                    {
                      Label: 'Buy\nAgain',
                      OnClick: buyAgain,
                      Type: 'BUY-AGAIN_ONCLICK',
                      Loading: loading.includes('BUY-AGAIN_ONCLICK')
                    },
                    {
                      Label: 'Raise\nEnquiry',
                      OnClick: onRaiseEnquiry,
                      Type: 'RAISE-ENQUIRY_ONCLICK',
                      Loading: loading.includes('RAISE-ENQUIRY_ONCLICK')
                    }
                  ]}
                  match={(x) => GetInTransitOrderActions(hasRefund, hasClaim, props.item).includes(x.Type)}
                  tag={''}
                />
              </>
            ) : (
              <>
                {!hasClaim && !hasRefund && (
                  <>
                    <PudoCodesAutoLoad
                      displayMode="full"
                      item={props.item}
                      actions={actions.Links}
                      wrapper={(x) => (
                        <SidePanelSection
                          title={t('YOUR_PIS_CODE_SUBTITLE', { defaultValue: 'Your Print In Store Code(s)' })}
                        >
                          <P2GBox py={1}>{x}</P2GBox>
                        </SidePanelSection>
                      )}
                    />
                    <Labels item={props.item} actions={actions} />
                    {!hasClaim && !hasRefund && actions.Links.find((x) => x.Type === 'ORDER-PROTECTION_PAGE') && (
                      <AsyncLinks
                        title={t('YOU_STILL_HAVE_TIME_SUBTITLE', { defaultValue: 'You still have time' })}
                        variant="button"
                        links={actions.Links}
                        match={(x) => x.Type === 'ORDER-PROTECTION_PAGE'}
                        tag={''}
                      >
                        <P2GText variant="body2">
                          {t('ADD_ADDITIONAL_EXTRAS_TEXT', {
                            defaultValue: 'You still have time to add additional extras to your order.'
                          })}
                        </P2GText>
                      </AsyncLinks>
                    )}
                  </>
                )}
                {showInvoice && (
                  <OrderInvoice
                    title={t('INVOICE_ACTION_SUBTITLE', { defaultValue: 'Invoice' })}
                    orderId={Number(props.item.BookingDetails.OrderId)}
                    wrapper={(x) => x}
                  />
                )}
                <AsyncLinks
                  title={t('OTHER_OPTIONS_SUBTITLE', { defaultValue: 'Other Options' })}
                  variant="list"
                  links={[
                    ...actions.Links,
                    SettingsCache.settings.Features.includes('ReturnCollection') && {
                      Label: 'Arrange\nReturn',
                      OnClick: arrangeReturn,
                      Type: 'ARRANGE-RETURN_ONCLICK',
                      Loading: loading.includes('ARRANGE-RETURN_ONCLICK')
                    },
                    {
                      Label: 'Buy\nAgain',
                      OnClick: buyAgain,
                      Type: 'BUY-AGAIN_ONCLICK',
                      Loading: loading.includes('BUY-AGAIN_ONCLICK')
                    },
                    !hasRefund && {
                      Label: 'Cancel\nOrder',
                      OnClick: onCancel,
                      Type: 'CANCEL_ONCLICK',
                      Loading: loading.includes('CANCEL_ONCLICK')
                    },
                    {
                      Label: 'Raise\nEnquiry',
                      OnClick: onRaiseEnquiry,
                      Type: 'RAISE-ENQUIRY_ONCLICK',
                      Loading: loading.includes('RAISE-ENQUIRY_ONCLICK')
                    }
                  ]}
                  match={(x) => GetOrderActions(hasRefund, hasClaim, props.item).includes(x.Type)}
                  tag={''}
                />
              </>
            )}
          </>
        ) : (
          <Loading />
        )}
      </SidePanelSections>
      <P2GBox mt={2} />
      <PanelButtons onCancel={props.close} />
    </P2GBox>
  );
};
